import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart,
  BarChart,
  PieChart,
  DonutChart,
} from "../../../common/ReusableComponent/Chart";
import {
  DollarSign,
  Users,
  Clock,
  TrendingUp,
  ReceiptText,
  Wallet,
  BarChart3,
  PieChart as PieChartIcon,
  Activity,
  CreditCard,
  AlertCircle,
  BadgeDollarSign,
} from "lucide-react";
import useApi from "../../../../utils/api-manager/Helper/useApi";

const safeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCount = (value) => safeNumber(value).toLocaleString("en-IN");

const formatMoney = (value) =>
  `₹${safeNumber(value).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const formatHours = (value) => {
  const parsed = safeNumber(value);
  return `${parsed.toFixed(parsed % 1 === 0 ? 0 : 1)}h`;
};

const getPayrollHealthTone = (totalPayrollCost, totalEmployees) => {
  if (!safeNumber(totalEmployees)) return "amber";
  if (!safeNumber(totalPayrollCost)) return "red";
  return "green";
};

const KpiCard = ({ icon: Icon, label, value, helper, tone = "blue" }) => (
  <div className={`paydb-kpi-card paydb-tone-${tone}`}>
    <div className="paydb-kpi-icon">
      <Icon size={21} />
    </div>
    <div className="paydb-kpi-content">
      <div className="paydb-kpi-label">{label}</div>
      <div className="paydb-kpi-value">{value}</div>
      {helper && <div className="paydb-kpi-helper">{helper}</div>}
    </div>
  </div>
);

const InsightCard = ({ icon: Icon, title, value, note, tone = "blue" }) => (
  <div className={`paydb-insight-card paydb-tone-${tone}`}>
    <div className="paydb-insight-top">
      <div className="paydb-insight-icon">
        <Icon size={19} />
      </div>
      <div>
        <h3>{title}</h3>
        <p>{note}</p>
      </div>
    </div>
    <div className="paydb-insight-value">{value}</div>
  </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="paydb-section-header">
    <div className="paydb-section-icon">
      <Icon size={18} />
    </div>
    <div>
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
    </div>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="paydb-empty-state">
    <AlertCircle size={26} />
    <span>{message}</span>
  </div>
);

const StatusPill = ({ children, tone = "blue" }) => (
  <span className={`paydb-pill paydb-pill-${tone}`}>{children}</span>
);

const DataTable = ({ columns, rows, emptyLabel }) => (
  <div className="paydb-table-wrap">
    {rows.length === 0 ? (
      <EmptyState message={emptyLabel} />
    ) : (
      <table className="paydb-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((column) => (
                <td key={column.key}>
                  {column.render ? column.render(row, rowIndex) : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export default function PayrollDashboard() {
  const { Post } = useApi();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPayrollData();
  }, []);

  const fetchPayrollData = async () => {
    try {
      setLoading(true);
      setError(null);

      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear}-01-01`;
      const endDate = `${currentYear}-12-31`;

      const response = await Post("payrollDashboardReport", {
        start_date: startDate,
        end_date: endDate,
      });

      setDashboardData(response?.data || {});
    } catch (fetchError) {
      console.error("Error fetching payroll data:", fetchError);
      setError("Failed to fetch payroll data");
    } finally {
      setLoading(false);
    }
  };

  const {
    monthly_salary_summary = [],
    top_employees_by_hours_worked = [],
    top_earning_employees = [],
    salary_distribution_based_on_unit = {},
    salary_distribution_based_on_pay_method = {},
    average_hours_per_employee = {},
    payment_statistics = {},
    payroll_summary = {},
  } = dashboardData || {};

  const totalPayrollCost = safeNumber(payroll_summary.total_payroll_cost);
  const totalEmployees = safeNumber(payroll_summary.total_employees_count);
  const averageHours = safeNumber(
    average_hours_per_employee.average_hours_per_employee
  );
  const totalHours = safeNumber(average_hours_per_employee.total_hours);
  const averagePayment = safeNumber(payment_statistics.average_payment_amount);
  const highestPayment = safeNumber(payment_statistics.highest_single_payment);
  const lowestPayment = safeNumber(payment_statistics.lowest_single_payment);
  const totalTransactions = safeNumber(payment_statistics.total_payment_transactions);
  const payrollHealthTone = getPayrollHealthTone(totalPayrollCost, totalEmployees);

  const monthlyLabels = useMemo(
    () =>
      monthly_salary_summary.map((item) =>
        new Date(item.month).toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        })
      ),
    [monthly_salary_summary]
  );

  const monthlyData = useMemo(
    () => monthly_salary_summary.map((item) => safeNumber(item.total_paid)),
    [monthly_salary_summary]
  );

  const topHoursRows = useMemo(
    () => top_employees_by_hours_worked.slice(0, 8),
    [top_employees_by_hours_worked]
  );

  const topEarningRows = useMemo(
    () => top_earning_employees.slice(0, 8),
    [top_earning_employees]
  );

  const topEmployeesLabels = topHoursRows.map((employee) => employee.employee_name);
  const topEmployeesHours = topHoursRows.map((employee) => safeNumber(employee.total_hours));
  const topEmployeesAmount = topHoursRows.map((employee) => safeNumber(employee.total_amount));

  const salaryUnitLabels = Object.keys(salary_distribution_based_on_unit);
  const salaryUnitData = Object.values(salary_distribution_based_on_unit).map(safeNumber);

  const payMethodLabels = Object.keys(salary_distribution_based_on_pay_method);
  const payMethodData = Object.values(salary_distribution_based_on_pay_method).map(safeNumber);

  const currentDateLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  if (loading) {
    return (
      <div className="responsive-box paydb-page">
        <style>{dashboardStyles}</style>
        <div className="paydb-loading">
          <div className="paydb-spinner" />
          <div>
            <h3>Loading Payroll Dashboard</h3>
            <p>Preparing payroll cost, hours and payment insights.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="responsive-box paydb-page">
        <style>{dashboardStyles}</style>
        <div className="paydb-empty-page paydb-error-page">
          <AlertCircle size={36} />
          <h2>{error}</h2>
          <p>Please try again after checking the payroll dashboard API.</p>
        </div>
      </div>
    );
  }

  if (!dashboardData || Object.keys(dashboardData).length === 0) {
    return (
      <div className="responsive-box paydb-page">
        <style>{dashboardStyles}</style>
        <div className="paydb-empty-page">
          <Users size={36} />
          <h2>No Payroll Data Found</h2>
          <p>Add employees and payroll records to start viewing payroll insights.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="responsive-box paydb-page">
      <style>{dashboardStyles}</style>

      <div className="paydb-shell">
        <section className="paydb-hero">
          <div>
            <div className="paydb-kicker">
              <span className="paydb-kicker-dot" />
              Payroll Business View
            </div>
            <h1>Payroll Dashboard</h1>
            <p>
              Simple executive view for payroll cost, employee workload, salary distribution and payment activity.
            </p>
            <div className="paydb-date-pill">{currentDateLabel}</div>
          </div>

          <div className="paydb-hero-kpis">
            <KpiCard
              icon={DollarSign}
              label="Total Payroll Cost"
              value={formatMoney(totalPayrollCost)}
              helper="Total salary payout in selected year"
              tone={payrollHealthTone}
            />
            <KpiCard
              icon={Users}
              label="Employees Paid"
              value={formatCount(totalEmployees)}
              helper="Employees included in payroll"
              tone="blue"
            />
            <KpiCard
              icon={Clock}
              label="Average Hours"
              value={formatHours(averageHours)}
              helper="Average hours per employee"
              tone="purple"
            />
          </div>
        </section>

        <section className="paydb-insight-grid">
          <InsightCard
            icon={ReceiptText}
            title="Avg Payment"
            value={formatMoney(averagePayment)}
            note="Average payout per transaction"
            tone="green"
          />
          <InsightCard
            icon={TrendingUp}
            title="Highest Payment"
            value={formatMoney(highestPayment)}
            note="Highest single payroll payment"
            tone="blue"
          />
          <InsightCard
            icon={Wallet}
            title="Lowest Payment"
            value={formatMoney(lowestPayment)}
            note="Lowest single payroll payment"
            tone="amber"
          />
          <InsightCard
            icon={Activity}
            title="Total Hours"
            value={formatHours(totalHours)}
            note={`${formatCount(totalTransactions)} payment transactions`}
            tone="purple"
          />
        </section>

        <section className="paydb-focus-grid">
          <div className="paydb-panel">
            <SectionHeader
              icon={BadgeDollarSign}
              title="Payroll Snapshot"
              subtitle="Important figures for business review"
            />

            <div className="paydb-snapshot-list">
              <div className="paydb-snapshot-row">
                <span>Total Payroll Cost</span>
                <strong>{formatMoney(totalPayrollCost)}</strong>
              </div>
              <div className="paydb-snapshot-row">
                <span>Total Employees</span>
                <strong>{formatCount(totalEmployees)}</strong>
              </div>
              <div className="paydb-snapshot-row">
                <span>Total Transactions</span>
                <strong>{formatCount(totalTransactions)}</strong>
              </div>
              <div className="paydb-snapshot-row">
                <span>Total Hours Worked</span>
                <strong>{formatHours(totalHours)}</strong>
              </div>
            </div>
          </div>

          <div className="paydb-panel">
            <SectionHeader
              icon={PieChartIcon}
              title="Payment Method Distribution"
              subtitle="How payroll payments were made"
            />

            <div className="paydb-chart-box">
              {payMethodLabels.length === 0 ? (
                <EmptyState message="No payment method data available" />
              ) : (
                <DonutChart
                  labels={payMethodLabels}
                  datasets={[
                    {
                      data: payMethodData,
                      backgroundColor: ["#2563eb", "#059669", "#d97706", "#7c3aed", "#dc2626"],
                    },
                  ]}
                  height="100%"
                  noShadow
                  noBorder
                  noMargin
                />
              )}
            </div>
          </div>
        </section>

        <section className="paydb-chart-grid">
          <div className="paydb-panel">
            <SectionHeader
              icon={BarChart3}
              title="Monthly Salary Trend"
              subtitle="Salary paid month-by-month"
            />

            <div className="paydb-chart-box paydb-chart-box-wide">
              {monthlyLabels.length === 0 ? (
                <EmptyState message="No monthly salary data available" />
              ) : (
                <LineChart
                  labels={monthlyLabels}
                  datasets={[
                    {
                      label: "Total Paid",
                      data: monthlyData,
                      backgroundColor: "#bfdbfe",
                      borderColor: "#2563eb",
                    },
                  ]}
                  height="100%"
                  noShadow
                  noBorder
                  noMargin
                />
              )}
            </div>
          </div>

          <div className="paydb-panel">
            <SectionHeader
              icon={Clock}
              title="Top Employees by Hours"
              subtitle="Workload and payout comparison"
            />

            <div className="paydb-chart-box paydb-chart-box-wide">
              {topEmployeesLabels.length === 0 ? (
                <EmptyState message="No employee hours data available" />
              ) : (
                <BarChart
                  labels={topEmployeesLabels}
                  datasets={[
                    {
                      label: "Hours Worked",
                      data: topEmployeesHours,
                      backgroundColor: "#93c5fd",
                    },
                    {
                      label: "Amount Earned",
                      data: topEmployeesAmount,
                      backgroundColor: "#86efac",
                    },
                  ]}
                  height="100%"
                  noShadow
                  noBorder
                  noMargin
                />
              )}
            </div>
          </div>
        </section>

        <section className="paydb-bottom-grid">
          <div className="paydb-panel">
            <SectionHeader
              icon={PieChartIcon}
              title="Salary Distribution by Unit"
              subtitle="Payroll distribution based on salary unit"
            />

            <div className="paydb-chart-box">
              {salaryUnitLabels.length === 0 ? (
                <EmptyState message="No salary unit data available" />
              ) : (
                <PieChart
                  labels={salaryUnitLabels}
                  datasets={[
                    {
                      data: salaryUnitData,
                      backgroundColor: ["#2563eb", "#059669", "#d97706", "#7c3aed", "#dc2626"],
                    },
                  ]}
                  height="100%"
                  noShadow
                  noBorder
                  noMargin
                />
              )}
            </div>
          </div>

          <div className="paydb-panel">
            <SectionHeader
              icon={CreditCard}
              title="Top Earning Employees"
              subtitle="Highest amount-per-hour employees"
            />

            <DataTable
              rows={topEarningRows}
              emptyLabel="No top earning employee data available"
              columns={[
                {
                  key: "employee_name",
                  label: "Employee",
                  render: (row) => <strong className="paydb-table-title">{row.employee_name}</strong>,
                },
                {
                  key: "amount_per_hour",
                  label: "Amount / Hour",
                  render: (row) => <StatusPill tone="green">{formatMoney(row.amount_per_hour)}</StatusPill>,
                },
              ]}
            />
          </div>
        </section>

        <section className="paydb-panel">
          <SectionHeader
            icon={Users}
            title="Top Employees by Hours Worked"
            subtitle="Compact list for quick payroll and workforce review"
          />

          <DataTable
            rows={topHoursRows}
            emptyLabel="No employee hours data available"
            columns={[
              {
                key: "employee_name",
                label: "Employee",
                render: (row) => <strong className="paydb-table-title">{row.employee_name}</strong>,
              },
              {
                key: "total_hours",
                label: "Hours",
                render: (row) => <StatusPill tone="blue">{formatHours(row.total_hours)}</StatusPill>,
              },
              {
                key: "total_amount",
                label: "Amount Earned",
                render: (row) => <span className="paydb-money">{formatMoney(row.total_amount)}</span>,
              },
            ]}
          />
        </section>
      </div>
    </div>
  );
}

const dashboardStyles = `
  .paydb-page {
    min-height: 100vh;
    padding: 10px 12px 28px;
    background:
      radial-gradient(circle at top left, rgba(37, 99, 235, 0.08), transparent 28%),
      radial-gradient(circle at top right, rgba(16, 185, 129, 0.07), transparent 28%),
      linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    color: #0f172a;
  }

  .paydb-shell {
    max-width: 1450px;
    margin: 0 auto;
  }

  .paydb-hero {
    position: relative;
    overflow: hidden;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(440px, 0.85fr);
    gap: 18px;
    align-items: center;
    padding: 22px;
    margin-bottom: 14px;
    border-radius: 28px;
    color: #ffffff;
    background: linear-gradient(135deg, #020617 0%, #111827 56%, #064e3b 100%);
    box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
  }

  .paydb-hero::after {
    content: "";
    position: absolute;
    width: 320px;
    height: 320px;
    right: -120px;
    top: -160px;
    border-radius: 999px;
    background: rgba(52, 211, 153, 0.16);
  }

  .paydb-hero > * {
    position: relative;
    z-index: 1;
  }

  .paydb-kicker {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 11px;
    border-radius: 999px;
    margin-bottom: 10px;
    font-size: 11px;
    font-weight: 950;
    color: #d1fae5;
    background: rgba(255, 255, 255, 0.13);
    border: 1px solid rgba(255, 255, 255, 0.22);
  }

  .paydb-kicker-dot {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: #22c55e;
    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.16);
  }

  .paydb-hero h1 {
    margin: 0;
    font-size: 31px;
    line-height: 1.08;
    font-weight: 950;
    letter-spacing: -0.045em;
  }

  .paydb-hero p {
    max-width: 700px;
    margin: 8px 0 0;
    color: rgba(255, 255, 255, 0.80);
    font-size: 13px;
    font-weight: 700;
    line-height: 1.6;
  }

  .paydb-date-pill {
    display: inline-flex;
    margin-top: 13px;
    padding: 7px 11px;
    border-radius: 999px;
    color: #bbf7d0;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.18);
    font-size: 11px;
    font-weight: 900;
  }

  .paydb-hero-kpis {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .paydb-kpi-card,
  .paydb-insight-card,
  .paydb-panel {
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid #dbe3ec;
    box-shadow: 0 16px 42px rgba(15, 23, 42, 0.075);
  }

  .paydb-kpi-card {
    display: flex;
    align-items: center;
    gap: 13px;
    min-height: 82px;
    padding: 13px;
    border-radius: 20px;
  }

  .paydb-kpi-icon,
  .paydb-insight-icon,
  .paydb-section-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--paydb-accent);
    background: var(--paydb-soft);
    border: 1px solid var(--paydb-border);
    flex-shrink: 0;
  }

  .paydb-kpi-icon {
    width: 44px;
    height: 44px;
    border-radius: 15px;
  }

  .paydb-kpi-label {
    color: #475569;
    font-size: 11px;
    font-weight: 950;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .paydb-kpi-value {
    margin-top: 2px;
    color: #0f172a;
    font-size: 21px;
    font-weight: 950;
    letter-spacing: -0.035em;
  }

  .paydb-kpi-helper {
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
  }

  .paydb-insight-grid,
  .paydb-focus-grid,
  .paydb-chart-grid,
  .paydb-bottom-grid {
    display: grid;
    gap: 14px;
    margin-bottom: 14px;
  }

  .paydb-insight-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .paydb-focus-grid,
  .paydb-chart-grid,
  .paydb-bottom-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .paydb-insight-card {
    border-radius: 22px;
    padding: 15px;
  }

  .paydb-insight-top {
    display: flex;
    gap: 11px;
    align-items: flex-start;
  }

  .paydb-insight-icon {
    width: 42px;
    height: 42px;
    border-radius: 15px;
  }

  .paydb-insight-card h3 {
    margin: 0;
    color: #0f172a;
    font-size: 14px;
    font-weight: 950;
  }

  .paydb-insight-card p {
    margin: 4px 0 0;
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
    line-height: 1.45;
  }

  .paydb-insight-value {
    margin-top: 14px;
    color: #0f172a;
    font-size: 24px;
    font-weight: 950;
    letter-spacing: -0.04em;
  }

  .paydb-panel {
    border-radius: 24px;
    padding: 16px;
    margin-bottom: 14px;
  }

  .paydb-section-header {
    display: flex;
    align-items: center;
    gap: 11px;
    margin-bottom: 14px;
  }

  .paydb-section-icon {
    width: 40px;
    height: 40px;
    border-radius: 15px;
    --paydb-accent: #0f172a;
    --paydb-soft: #f8fafc;
    --paydb-border: #dbe3ec;
  }

  .paydb-section-header h2 {
    margin: 0;
    color: #0f172a;
    font-size: 17px;
    font-weight: 950;
    letter-spacing: -0.025em;
  }

  .paydb-section-header p {
    margin: 3px 0 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
  }

  .paydb-snapshot-list {
    display: grid;
    gap: 10px;
  }

  .paydb-snapshot-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border-radius: 16px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
  }

  .paydb-snapshot-row span {
    color: #475569;
    font-size: 12px;
    font-weight: 850;
  }

  .paydb-snapshot-row strong {
    color: #0f172a;
    font-size: 15px;
    font-weight: 950;
  }

  .paydb-chart-box {
    height: 285px;
    min-height: 285px;
  }

  .paydb-chart-box-wide {
    height: 310px;
  }

  .paydb-table-wrap {
    width: 100%;
    overflow-x: auto;
  }

  .paydb-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0 7px;
    min-width: 560px;
  }

  .paydb-table th {
    padding: 10px 12px;
    color: #64748b;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    font-size: 11px;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }

  .paydb-table th:first-child {
    border-left: 1px solid #e2e8f0;
    border-radius: 13px 0 0 13px;
  }

  .paydb-table th:last-child {
    border-right: 1px solid #e2e8f0;
    border-radius: 0 13px 13px 0;
  }

  .paydb-table td {
    padding: 11px 12px;
    background: #ffffff;
    border-top: 1px solid #e2e8f0;
    border-bottom: 1px solid #e2e8f0;
    color: #334155;
    font-size: 13px;
    font-weight: 750;
    vertical-align: middle;
  }

  .paydb-table td:first-child {
    border-left: 1px solid #e2e8f0;
    border-radius: 13px 0 0 13px;
  }

  .paydb-table td:last-child {
    border-right: 1px solid #e2e8f0;
    border-radius: 0 13px 13px 0;
  }

  .paydb-table-title {
    color: #0f172a;
    font-weight: 950;
  }

  .paydb-money {
    color: #059669;
    font-weight: 950;
  }

  .paydb-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 48px;
    padding: 5px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 950;
    white-space: nowrap;
  }

  .paydb-pill-blue {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  .paydb-pill-green {
    background: #ecfdf5;
    color: #047857;
    border: 1px solid #a7f3d0;
  }

  .paydb-empty-state {
    min-height: 190px;
    border: 1px dashed #cbd5e1;
    border-radius: 18px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: #64748b;
    font-size: 13px;
    font-weight: 850;
    background: #f8fafc;
  }

  .paydb-empty-page {
    min-height: 430px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    color: #0f172a;
  }

  .paydb-empty-page svg {
    color: #2563eb;
    margin-bottom: 14px;
  }

  .paydb-error-page svg {
    color: #dc2626;
  }

  .paydb-empty-page h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 950;
  }

  .paydb-empty-page p {
    max-width: 480px;
    margin: 8px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }

  .paydb-tone-blue {
    --paydb-accent: #2563eb;
    --paydb-soft: #eff6ff;
    --paydb-border: #bfdbfe;
  }

  .paydb-tone-green {
    --paydb-accent: #059669;
    --paydb-soft: #ecfdf5;
    --paydb-border: #a7f3d0;
  }

  .paydb-tone-purple {
    --paydb-accent: #7c3aed;
    --paydb-soft: #f3e8ff;
    --paydb-border: #ddd6fe;
  }

  .paydb-tone-amber {
    --paydb-accent: #d97706;
    --paydb-soft: #fffbeb;
    --paydb-border: #fde68a;
  }

  .paydb-tone-red {
    --paydb-accent: #dc2626;
    --paydb-soft: #fef2f2;
    --paydb-border: #fecaca;
  }

  .paydb-loading {
    min-height: 420px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
    color: #0f172a;
  }

  .paydb-loading h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 950;
  }

  .paydb-loading p {
    margin: 5px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }

  .paydb-spinner {
    width: 54px;
    height: 54px;
    border-radius: 999px;
    border: 5px solid #dcfce7;
    border-top-color: #059669;
    animation: paydb-spin 0.8s linear infinite;
  }

  @keyframes paydb-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 1200px) {
    .paydb-hero {
      grid-template-columns: 1fr;
    }

    .paydb-insight-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .paydb-focus-grid,
    .paydb-chart-grid,
    .paydb-bottom-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .paydb-page {
      padding: 8px 8px 22px;
    }

    .paydb-hero {
      border-radius: 22px;
      padding: 18px;
    }

    .paydb-hero h1 {
      font-size: 25px;
    }

    .paydb-insight-grid {
      grid-template-columns: 1fr;
    }

    .paydb-panel,
    .paydb-insight-card {
      border-radius: 20px;
      padding: 14px;
    }

    .paydb-chart-box,
    .paydb-chart-box-wide {
      height: 280px;
      min-height: 280px;
    }
  }
`;
